import { buildSafePageLocation } from '../../utils/detailedResultsStorage';

import { readVisitorId } from '../visitorId';
import { AnalyticsProvider } from './AnalyticsProvider';

const LINKER_DOMAINS = [
  'audicus.com',
  'www.audicus.com',
  'hearing-test.audicus.com',
];

function ensureGtagStub(): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') {
    return;
  }
  window.gtag = function gtag() {
    // Official gtag.js queue format — must push `arguments`, not a rest array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
}

function cookieFlagEnabled(decoded: string, flag: string): boolean {
  const match = decoded.match(new RegExp(`${flag}:(true|false)`, 'i'));
  return match?.[1]?.toLowerCase() === 'true';
}

export const parseCookiebotConsent = (
  cookieString: string = typeof document === 'undefined' ? '' : document.cookie,
): Record<string, 'granted' | 'denied'> | null => {
  const match = cookieString
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('CookieConsent='));
  if (!match) return null;

  let decoded = match.slice('CookieConsent='.length);
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    // Use the raw cookie value.
  }

  const ads = cookieFlagEnabled(decoded, 'marketing') ? 'granted' : 'denied';
  return {
    analytics_storage: cookieFlagEnabled(decoded, 'statistics')
      ? 'granted'
      : 'denied',
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  };
};

/** true / false after a Cookiebot choice; null if the visitor has not responded. */
export const hasStatisticsConsent = (
  cookieString: string = typeof document === 'undefined' ? '' : document.cookie,
): boolean | null => {
  const settings = parseCookiebotConsent(cookieString);
  if (settings) {
    return settings.analytics_storage === 'granted';
  }
  if (window.Cookiebot?.hasResponse) {
    return Boolean(window.Cookiebot.consent?.statistics);
  }
  return null;
};

const syncConsentFromCookiebot = () => {
  if (typeof window.gtag !== 'function') return;
  const settings = parseCookiebotConsent();
  if (!settings) return;
  window.gtag('consent', 'update', settings);
};

export class GoogleAnalyticsProvider implements AnalyticsProvider {
  name = 'google-analytics';

  initialize(): void {
    ensureGtagStub();
    this.loadCookiebot();
    window.addEventListener('CookiebotOnConsentReady', syncConsentFromCookiebot);
    window.addEventListener('CookiebotOnAccept', syncConsentFromCookiebot);
    window.addEventListener('CookiebotOnDecline', syncConsentFromCookiebot);
    syncConsentFromCookiebot();
    this.loadGoogleAnalyticsScript();
    this.configure();
  }

  private loadCookiebot(): void {
    const cbid = process.env.REACT_APP_COOKIEBOT_ID;
    if (!cbid || document.getElementById('Cookiebot')) {
      return;
    }
    const script = document.createElement('script');
    script.id = 'Cookiebot';
    script.src = 'https://consent.cookiebot.com/uc.js';
    script.setAttribute('data-cbid', cbid);
    script.setAttribute('data-blockingmode', 'auto');
    script.type = 'text/javascript';
    document.head.appendChild(script);
  }

  private measurementId(): string | undefined {
    return process.env.REACT_APP_GA_TRACKING_ID;
  }

  private loadGoogleAnalyticsScript(): void {
    const measurementId = this.measurementId();
    if (!measurementId) {
      console.error('Google Analytics measurement ID is missing');
      return;
    }

    if (document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      return;
    }

    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    gtagScript.onload = () => console.log('Google Analytics loaded');
    gtagScript.onerror = () => console.error('Google Analytics failed to load');
    document.head.appendChild(gtagScript);
  }

  private cookieDomain(): string {
    return window.location.hostname.endsWith('audicus.com')
      ? 'audicus.com'
      : 'none';
  }

  private configure(): void {
    const measurementId = this.measurementId();
    if (!measurementId || typeof window.gtag !== 'function') {
      return;
    }

    const visitorId = readVisitorId(document.cookie);

    window.gtag('js', new Date());
    window.gtag('set', 'url_passthrough', true);
    window.gtag('config', measurementId, {
      cookie_domain: this.cookieDomain(),
      linker: {
        domains: LINKER_DOMAINS,
        accept_incoming: true,
      },
      page_location: buildSafePageLocation(),
      ...(visitorId ? { user_properties: { visitor_id: visitorId } } : {}),
    });
  }

  track(event: string, properties?: Record<string, any>): void {
    if (typeof window.gtag !== 'function') {
      return;
    }
    if (properties && Object.keys(properties).length > 0) {
      window.gtag('event', event, properties);
      return;
    }
    window.gtag('event', event);
  }

  identify(userId: string): void {
    const measurementId = this.measurementId();
    if (typeof window.gtag !== 'function' || !measurementId) {
      return;
    }
    window.gtag('config', measurementId, {
      user_id: userId,
    });
  }

  page(name?: string): void {
    const measurementId = this.measurementId();
    if (typeof window.gtag !== 'function' || !measurementId) {
      return;
    }
    window.gtag('config', measurementId, {
      page_title: name,
      page_location: buildSafePageLocation(),
    });
  }
}
